import { AfterViewInit, Directive, ElementRef, EventEmitter, OnDestroy, Output, Input, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Subject, fromEvent, filter, takeUntil } from 'rxjs';

/**
 * This directive fires an event when focusout is detected to an element which is not the one the directive is attached.
 *
 * @example
 * <code>
 *   <pre>
 *    <div (fcomFocusOut)="doSomething()">
 *      Some content
 *     </div>
 *   </pre>
 * </code>
 */
@Directive({
  selector: '[fcomFocusOut]',
  standalone: false,
})
export class FocusOutDirective implements AfterViewInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT, { optional: true });

  /**
   * Exception of the focusout event.
   * An element reference to ignore.
   */
  @Input() focusOutException: ElementRef;

  @Output()
  fcomFocusOut: EventEmitter<Event> = new EventEmitter();

  private ngDestroyed$ = new Subject();

  ngAfterViewInit(): void {
    if (!this.document) {
      return;
    }
    setTimeout(() => {
      const source$ = fromEvent(this.document, 'focusout');
      source$
        .pipe(
          filter((event: FocusEvent) => {
            if (!(event.relatedTarget instanceof Element)) {
              return false;
            }

            const isTargetOutside = !(this.elementRef.nativeElement as Element).contains(event.relatedTarget);
            if (this.focusOutException) {
              return (
                isTargetOutside && !(this.focusOutException.nativeElement as Element).contains(event.relatedTarget)
              );
            }
            return isTargetOutside;
          }),
          takeUntil(this.ngDestroyed$)
        )
        .subscribe((event) => this.fcomFocusOut.emit(event));
    });
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }
}
